import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import { PATHS } from '@/constants/Paths';
import { buildCircuitsListSearch } from '@/utils/searchParams/buildCircuitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import StyledStepper from '@/components/styled/StyledStepper';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import HorizontalLine from '@/components/ui/HorizontalLine';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { useCreateCircuit, useUpdateCircuit } from '@/hooks/useCircuits';
import { Label } from '@/components/ui/label';
import { ArrowRightIcon } from 'lucide-react';
import CircuitDaysList from '@/components/circuits/CircuitDaysList';
import { createEmptyCircuitDays } from '@/utils/circuits/createEmptyCircuitDays';
import { parseCircuitDaysToSubmit } from '@/utils/circuits/parseCircuitDaysToSubmit';
import CircuitSummary from '@/components/circuits/CircuitSummary';
import clsx from 'clsx';
import { useToast } from '@/hooks/useToast';
import CircuitConfiguration from '@/components/circuits/CircuitConfiguration';
import CircuitFormDetailsView from '@/components/circuits/CircuitFormDetailsView';
import { CIRCUIT_STEPS } from '@/constants/steppers/circuitSteps';
import { useCircuitPrices } from '@/hooks/useCircuitPrices';
import { getCircuitTotalPrice } from '@/utils/circuits/prices/getCircuitTotalPrice';
import { startOfTomorrow } from 'date-fns';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import {
  CircuitFormDataWithTranslations,
  CircuitSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useTranslateFields } from '@/hooks/useTranslate';
import { useResourceLanguage } from '@/hooks/useResourceLanguage';
import LanguageSwitchWithTranslateBtn from '@/components/translation/LanguageSwitchWithTranslateBtn';
import { TRANSLATABLE_FIELDS } from '@/i18n/translatable-fields';
import { appendCircuitParticipantCountsToFormData } from '@/utils/circuits/participantCounts';
import { saveCircuitErrorToast } from '@/utils/circuits/saveCircuitError';

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<CircuitFormDataWithTranslations>;

const CreateCircuitPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const methods = useForm<CircuitFormDataWithTranslations>({
    mode: 'onChange',
    resolver: zodResolver(CircuitSchemaWithTranslations),
    defaultValues: {
      name: undefined,
      countries: [
        {
          country: undefined,
          currency: undefined,
        },
      ],
      childAges: [
        {
          childAgeFrom: 2,
          childAgeTo: 11,
        },
      ],
      mainImage: undefined,
      images: [],
      additionalParagraphs: [],
      circuitDays: createEmptyCircuitDays(1),
    },
  });

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { isValid, errors },
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedCountries = useWatch({ control, name: 'countries' });
  const numberOfDays = useWatch({ control, name: 'nbDays' });
  const numberOfParticipants = useWatch({ control, name: 'nbParticipants' });

  useEffect(() => {
    if (numberOfDays && numberOfDays >= 1) {
      setValue('nbNights', numberOfDays - 1);
    }
  }, [numberOfDays, setValue]);

  const [currStep, setCurrStep] = useState(0);
  const [circuitId, setCircuitId] = useState<string | undefined>();

  const { mutateAsync: updateCircuit } = useUpdateCircuit();
  const { mutateAsync: createCircuit } = useCreateCircuit();

  const isSaveDisabled = useMemo(
    () =>
      !currentName ||
      selectedCountries[0].country == undefined ||
      !numberOfDays ||
      !numberOfParticipants,
    [currentName, numberOfDays, numberOfParticipants, selectedCountries],
  );

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { language, setLanguage } = useResourceLanguage();
  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: TRANSLATABLE_FIELDS.Circuit,
    nestedFields: {
      circuitDays: ['stepTitle', 'description'],
    },
  });

  const circuitDayErrorIndex = useMemo(() => {
    if (errors.circuitDays) {
      return +Object.keys(errors.circuitDays)[0];
    }
    return null;
  }, [errors.circuitDays]);

  const hasNavigatedToErrorStepRef = useRef(false);
  const prevHasErrorsRef = useRef(false);

  useEffect(() => {
    const hasErrors = !!errors.circuitDays;

    if (
      hasErrors &&
      !prevHasErrorsRef.current &&
      !hasNavigatedToErrorStepRef.current
    ) {
      startTransition(() => {
        setCurrStep(1);
      });
      hasNavigatedToErrorStepRef.current = true;
    }

    if (!hasErrors && prevHasErrorsRef.current) {
      hasNavigatedToErrorStepRef.current = false;
    }

    prevHasErrorsRef.current = hasErrors;
  }, [errors.circuitDays]);

  const { toastAsync } = useToast();

  const onSubmit: SubmitFormType = (isDraft) => {
    return async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      addIf(data.remarks, 'remarks');
      data.countries.forEach(({ country }) => {
        addIf(country?.value, 'countries[]');
      });
      data.countries.forEach(({ currency }) => {
        addIf(currency?.value, 'currencies[]');
      });
      formData.append(
        'marginRate',
        data.marginRate !== undefined ? String(data.marginRate) : '',
      );
      if (data.nbDays) formData.append('nbDays', String(data.nbDays));
      if (data.nbNights) formData.append('nbNights', String(data.nbNights));
      appendCircuitParticipantCountsToFormData(formData, {
        nbParticipants: data.nbParticipants,
        nbAdolescents: data.nbAdolescents,
        nbChildren: data.nbChildren,
      });
      if (data.childAges && data.childAges.length) {
        formData.append('childAgeFrom', String(data.childAges[0].childAgeFrom));
        formData.append('childAgeTo', String(data.childAges[0].childAgeTo));
        if (data.childAges.length > 1) {
          formData.append(
            'secondChildAgeFrom',
            String(data.childAges[1].childAgeFrom),
          );
          formData.append(
            'secondChildAgeTo',
            String(data.childAges[1].childAgeTo),
          );
        }
      }
      if (data.mainImage) {
        formData.append('files', data.mainImage);
        formData.append('hasMainImage', true.toString());
      } else formData.append('hasMainImage', false.toString());
      if (data.images && data.images.length) {
        data.images
          .filter((image) => image instanceof Blob)
          .forEach((image) => formData.append('files', image));
        formData.append(
          'imagesCount',
          data.images
            .filter((image) => image instanceof Blob)
            .length.toString(),
        );
      }
      addIf(data.summary, 'summary');
      addIf(data.importantPoints, 'importantPoints');
      if (data.additionalParagraphs) {
        formData.append(
          'additionalParagraphs',
          JSON.stringify(data.additionalParagraphs),
        );
      }
      addIf(data.priceIncludes, 'priceIncludes');
      addIf(data.priceExcludes, 'priceExcludes');
      addIf(data.modificationsConditions, 'modificationConditions');
      addIf(data.cancellationConditions, 'cancellationConditions');
      if (data.circuitDays) {
        formData.append(
          'circuitDays',
          JSON.stringify(parseCircuitDaysToSubmit(data.circuitDays)),
        );
        data.circuitDays.forEach((circuitDay) => {
          circuitDay.images
            ?.filter((image) => image instanceof Blob)
            .forEach((image) => formData.append('files', image));
        });
      }
      formData.append('translations', JSON.stringify(data.translations));
      if (circuitId) {
        await toastAsync(updateCircuit({ circuitId, circuit: formData }), {
          success: t('circuits.viewForm.circuitUpdatedSuccess'),
          error: saveCircuitErrorToast(t),
        });
      } else {
        const result = await toastAsync(createCircuit(formData), {
          success: t('circuits.viewForm.circuitCreatedSuccess'),
          error: saveCircuitErrorToast(t),
        });
        setCircuitId(result.id);
      }
      if (!isDraft)
        navigate(
          PATHS.home.circuits.list +
            getListSearchFromLocation(location, buildCircuitsListSearch),
        );
    };
  };

  const circuitDays = useWatch({ control, name: 'circuitDays' });

  const { circuitDaysPriceByStartDate } = useCircuitPrices({
    days: circuitDays,
    circuitMarginRate: useWatch({ control, name: 'marginRate' }),
    nbParticipants: Number(numberOfParticipants),
    nbChildren: Number(useWatch({ control, name: 'nbChildren' })),
    nbAdolescents: Number(useWatch({ control, name: 'nbAdolescents' })),
    targetCurrency: selectedCountries[0].currency?.value,
    enabled: currStep >= 2,
  });

  const dayWithMinimumPrice = useMemo(
    () =>
      currStep >= 2
        ? getCircuitTotalPrice(circuitDaysPriceByStartDate).date
        : null,
    [circuitDaysPriceByStartDate, currStep],
  );

  const [selectedArrivalDate, setSelectedArrivalDate] = useState(
    dayWithMinimumPrice ?? startOfTomorrow(),
  );

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || t('circuits.viewForm.newCircuit')}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('circuits.viewForm.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.circuits.list +
                    getListSearchFromLocation(
                      location,
                      buildCircuitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('circuits.viewForm.cancel')}
            onClick={() =>
              navigate(
                PATHS.home.circuits.list +
                  getListSearchFromLocation(location, buildCircuitsListSearch),
              )
            }
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
          />
          <StyledButtonLoader
            title={t('circuits.viewForm.saveAsDraft')}
            disabled={isSaveDisabled}
            successTitle={t('circuits.viewForm.draftSaved')}
            onClick={() => onSubmit(true)(getValues())}
            className="font-semibold gap-1 px-3 py-2"
            icon={<SaveIcon className="w-5 h-5" />}
          />
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(CIRCUIT_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
          disableNext={isSaveDisabled}
          disableConditions={{
            4: !selectedArrivalDate,
          }}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({
              allNestedFields: true,
              translateToAllLocales: true,
            })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
          hideTranslateBtn={currStep > 1}
        />
        {currStep === 0 ? (
          <>
            {errorsLength > 0 && (
              <Label className="text-error-500">
                {t('circuits.viewForm.checkRequiredFields')}
              </Label>
            )}
            <CircuitConfiguration
              control={control}
              isEditEnabled
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
            />
            <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-16">
              <StyledButtonLoader
                title={t('circuits.viewForm.saveAsDraft')}
                disabled={isSaveDisabled}
                successTitle={t('circuits.viewForm.draftSaved')}
                onClick={() => onSubmit(true)(getValues())}
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </div>
            <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('circuits.viewForm.validateAndCreateSteps')}
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
                disabled={isSaveDisabled}
                className="font-semibold gap-1 px-3 py-2"
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
              />
            </div>
          </>
        ) : currStep === 1 ? (
          <div className="relative">
            <CircuitDaysList
              control={control}
              language={language}
              circuitDayErrorIndex={circuitDayErrorIndex}
            />
            <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-16">
              <StyledButtonLoader
                title={t('circuits.viewForm.saveAsDraft')}
                disabled={isSaveDisabled}
                successTitle={t('circuits.viewForm.draftSaved')}
                onClick={() => onSubmit(true)(getValues())}
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </div>
            <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('circuits.viewForm.showGlobalPricing')}
                onClick={() => {
                  setCurrStep(2);
                  window.scrollTo(0, 0);
                }}
                disabled={isSaveDisabled}
                className="font-semibold gap-1 px-3 py-2"
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
              />
            </div>
          </div>
        ) : currStep === 2 ? (
          <>
            <CircuitSummary
              control={control}
              selectedArrivalDate={selectedArrivalDate}
              setSelectedArrivalDate={setSelectedArrivalDate}
              language={language}
            />
            <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
              <StyledButton
                title={t('circuits.viewForm.seeDescription')}
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(3);
                  window.scrollTo(0, 0);
                }}
                icon={<ArrowRightIcon className="size-5" />}
                iconPosition="right"
              />
            </div>
          </>
        ) : currStep >= 3 ? (
          <>
            <CircuitFormDetailsView
              control={control}
              circuitId={circuitId ?? ''}
              circuitCurrency={selectedCountries[0].currency?.value ?? 'EUR'}
              currStep={currStep}
              setCurrStep={setCurrStep}
              selectedArrivalDate={selectedArrivalDate}
              setSelectedArrivalDate={setSelectedArrivalDate}
              publishCircuit={handleSubmit(onSubmit(false))}
              errorsLength={errorsLength}
              isValid={isValid}
              language={language}
            />
            {currStep === 3 && (
              <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
                <StyledButton
                  title={t('circuits.viewForm.seeOptions')}
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={() => {
                    setCurrStep(4);
                    window.scrollTo(0, 0);
                  }}
                  disabled={!selectedArrivalDate}
                  icon={<ArrowRightIcon className="size-5" />}
                  iconPosition="right"
                />
              </div>
            )}
            {currStep === 4 && (
              <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
                <StyledButtonLoader
                  title={t('circuits.viewForm.publishCircuit')}
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={handleSubmit(onSubmit(false))}
                  successTitle={t('circuits.viewForm.publishedSuccess')}
                  hasSucceeded={!errorsLength}
                  errorTitle={t('circuits.viewForm.checkRequiredFields')}
                  icon={
                    <PlusIcon
                      className={clsx('size-5', {
                        'fill-white': isValid,
                        'fill-gray-400': !isValid,
                      })}
                    />
                  }
                  iconPosition="right"
                />
              </div>
            )}
          </>
        ) : null}
      </StyledContainer>
    </FormProvider>
  );
};

export default CreateCircuitPage;
